import { useCallback, useMemo } from 'react';
import { useUserContext, canAccessView } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePlatform } from '@/hooks/usePlatform';
import type { AdminPermissions, OuScope, UserModules } from '@/api/types';
import type { UserRole } from '@/hooks/useCurrentUser';

export type ViewId = 'survey' | 'safe' | 'fundamentals' | 'admin' | 'executive';

export interface NavLink {
  view: ViewId;
  icon: string;
  /** Icono del menú inferior de móvil cuando difiere del de escritorio. */
  mobileIcon?: string;
  labelKey: string;
}

/** Entradas del menú principal, en el orden en que se muestran (escritorio y móvil).
 *  2026-10-29: "Administradores" (antes vista `users`, gestión de admins GLOBALES) salió del lateral:
 *  ahora es la sección `administradores` del grupo Admin General, dentro de Administración. */
export const NAV_LINKS: NavLink[] = [
  { view: 'survey',       icon: 'fa-home',      mobileIcon: 'fa-clipboard-list', labelKey: 'nav.home'         },
  { view: 'safe',         icon: 'fa-seedling',  labelKey: 'nav.growth'       },
  { view: 'fundamentals', icon: 'fa-compass',   labelKey: 'nav.fundamentals' },
  { view: 'admin',        icon: 'fa-cog',       labelKey: 'nav.admin'        },
  { view: 'executive',    icon: 'fa-users',     labelKey: 'nav.executive'    },
];

/**
 * Sin perfil resuelto (modo mock / simulador de demo) no hay permisos del backend que aplicar:
 * se asumen concedidos los cuatro para que el rol simulado siga siendo navegable. En producción
 * siempre hay perfil, así que este valor no se usa.
 */
const DEMO_PERMISSIONS: AdminPermissions = {
  canCreateSurveys: true,
  canViewAnalytics: true,
  canManageUsers: true,
  canApproveSurveys: true,
  canAccessCatalogFundamentals: true,
  canAccessCatalogOus: true,
};

/**
 * Sin perfil resuelto (modo mock / simulador de demo) se asumen habilitados los dos módulos,
 * para que el rol simulado pueda recorrer el menú completo. En producción siempre hay perfil.
 */
const DEMO_MODULES: UserModules = { canViewPulso: true, canViewBrillamos: true };

/** Sin perfil resuelto (modo mock / simulador de demo) no hay alcance real que aplicar: se asume
 *  sin restricción, igual que DEMO_PERMISSIONS/DEMO_MODULES, para que el rol simulado navegue libre. */
const DEMO_OU_SCOPE: OuScope = { rootOuIds: [], allOusAccess: true };

export interface VisibleNavLinks {
  links: NavLink[];
  /** Todos los roles del usuario — es lo que decide el acceso. */
  activeRoles: UserRole[];
  /** Rol de mayor privilegio, solo para etiquetas de UI. */
  activeRole: UserRole;
  permissions: AdminPermissions;
  modules: UserModules;
  /**
   * Permiso efectivo del módulo Reconocimiento (null mientras el perfil no se ha resuelto). Gatea
   * las 12 secciones del bloque "Fundamentales" — ver `getAccessibleRecognitionSections`.
   */
  recognitionPermissions: readonly string[] | null;
  /**
   * Alcance de OU del solicitante sobre Reconocimiento/Brillamos (`AppUser.RecognitionScopeOuIds`,
   * ya expandido a descendientes). Distinto del alcance de Pulso — un `AdminOUBrillamos` no
   * administra Pulso. Lo usa la pantalla "Roles y Permisos" de Brillamos para acotar, a un
   * `AdminOUBrillamos` sin rol tenant-wide, qué administradores puede ver/editar y qué OUs puede
   * ofrecer en el selector de "Alcance". Opcional en el tipo solo para no romper los mocks de
   * `useVisibleNavLinks` de pantallas ajenas a Brillamos que no lo declaran — el hook real
   * siempre lo puebla (ver más abajo); los consumidores deben aplicar su propio valor por defecto.
   */
  recognitionOuScope?: OuScope;
  /**
   * `true` cuando ya se puede decidir acceso: el perfil autenticado resolvió (éxito o fallo), o
   * no hay identidad que resolver (modo demo/sin auth → `currentUser === null`). `false` SOLO
   * durante la ventana de carga de un perfil real. Los guards que redirigen por falta de acceso
   * DEBEN esperar a que sea `true`: mientras carga, los roles caen a Employee y botarían a un
   * admin legítimo (bug de rebote de `/app/admin` en QA).
   */
  profileResolved: boolean;
  isViewVisible: (view: ViewId) => boolean;
}

/**
 * Entradas del menú visibles para el usuario actual. Una sola fuente para el menú lateral y el
 * menú inferior de móvil, de modo que ambos apliquen exactamente las mismas reglas de acceso.
 */
export function useVisibleNavLinks(): VisibleNavLinks {
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();
  // 🔴 BUG (2026-09-08, "Administración se atora en /app/reconocer") — `usePlatform` es un hook con
  // estado LOCAL por instancia: en un montaje nuevo (p. ej. AdminView.tsx de Reconocimiento, que solo
  // existe la primera vez que se navega a /app/admin) su propio `teamsContext` arranca en null en el
  // primer render, así que `useCurrentUser()` no tiene todavía ni `id` ni `email` — `hasIdentity` es
  // false y el hook devuelve `null`, EXACTAMENTE igual que en modo demo/sin auth. Sin `isReady`, este
  // hook no podía distinguir "estamos en Teams pero la identidad aún no llega" de "no hay Teams ni
  // MSAL, es demo" — y trataba la primera ventana como si ya hubiera decidido, con los roles vacíos
  // de demo. Un guard que redirige de inmediato al ver `profileResolved=true` (como el de AdminView de
  // Reconocimiento) nunca le daba tiempo a la identidad real de llegar en el siguiente render: se
  // desmontaba antes. Ver el `platformReady` que ya usa AppRouter para el mismo problema.
  const { isReady: platformReady } = usePlatform();
  const resolvedRoles = currentUser?.roles;
  // `demoUser.role` solo aplica sin perfil resuelto (modo demo): ahí el simulador trabaja con un
  // único rol. Con perfil real manda siempre la lista completa.
  const activeRoles = useMemo(
    () => resolvedRoles ?? [demoUser.role],
    [resolvedRoles, demoUser.role],
  );
  const activeRole = currentUser?.primaryRole ?? demoUser.role;
  const permissions = currentUser?.permissions ?? DEMO_PERMISSIONS;
  const modules = currentUser?.modules ?? DEMO_MODULES;
  // Sin perfil resuelto (modo mock / simulador de demo) no hay permiso real que aplicar: null
  // hace que accessMatrix caiga al default de rol (RECOGNITION_DIRECT_ACCESS), igual que hoy.
  const recognitionPermissions = currentUser?.recognitionPermissions ?? null;
  const recognitionOuScope = currentUser?.recognitionOuScope ?? DEMO_OU_SCOPE;

  const isViewVisible = useCallback(
    (view: ViewId) => canAccessView(view, activeRoles, permissions, modules, recognitionPermissions),
    [activeRoles, permissions, modules, recognitionPermissions],
  );

  const links = useMemo(() => NAV_LINKS.filter(({ view }) => isViewVisible(view)), [isViewVisible]);

  // `?? platformReady` (antes `?? true`): sin `currentUser` puede ser modo demo/sin auth GENUINO
  // (nada que esperar, los roles salen de `demoUser` de forma síncrona) o puede ser Teams/MSAL
  // TODAVÍA resolviendo identidad en este montaje — y `usePlatform` es justo lo que distingue una
  // cosa de la otra. Con `currentUser` real, se respeta su propia bandera (false durante la carga).
  const profileResolved = currentUser?.profileResolved ?? platformReady;

  return {
    links, activeRoles, activeRole, permissions, modules, recognitionPermissions, recognitionOuScope,
    profileResolved, isViewVisible,
  };
}
